import { Icon, IconsEnum } from 'components/Icons';
import { InputHTMLAttributes } from 'react';
import { Controller, FieldValues, Path, UseControllerProps } from 'react-hook-form';
import { isControlledInput } from '../utils';
import styles from './selectInput.module.scss';
export type SelectOption = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

export type SelectProps = {
  options: Array<SelectOption>;
  label: string;
  className?: string;
  hasMarginRight?: boolean;
  hasMarginBottom?: boolean;
  hasMarginTop?: boolean;
  hasMarginLeft?: boolean;
} & InputHTMLAttributes<HTMLSelectElement>;

export type ControlledSelectInputProps<T extends FieldValues = FieldValues, K extends Path<T> = Path<T>> = SelectProps &
  UseControllerProps<T, K>;

export function SelectInput<T extends FieldValues = FieldValues>(props: ControlledSelectInputProps<T>): JSX.Element;
export function SelectInput(props: SelectProps): JSX.Element;
export function SelectInput(props: ControlledSelectInputProps | SelectProps): JSX.Element {
  if (isControlledInput<ControlledSelectInputProps>(props)) {
    const { hasMarginTop, hasMarginRight, hasMarginBottom, hasMarginLeft, ...defaultProps } = props;
    return (
      <Controller
        control={props.control}
        name={props.name}
        rules={props.rules}
        key={`${props.name}_${props.value}`}
        defaultValue={props.value}
        render={({ field: { value, onChange, ref, onBlur }, fieldState: { error } }) => (
          <div
            className={`${styles.container}
          ${hasMarginTop ? styles.containerMarginTop : ''}
          ${hasMarginRight ? styles.containerMarginRight : ''}
          ${hasMarginBottom ? styles.containerMarginBottom : ''}
          ${hasMarginLeft ? styles.containerMarginLeft : ''}
          `}
          >
            <label htmlFor={props.label} className={styles.label}>
              {props.label}
            </label>
            <div className={`${styles.selectWrapper} ${props.className}`}>
              <select
                className={`${styles.select} ${error && styles.inputError}`}
                ref={ref}
                id={props.name}
                value={value || ''}
                disabled={props.disabled}
                onChange={(e) => {
                  onChange(e);
                  props.onChange && props.onChange(e);
                }}
                onBlur={(e) => {
                  onBlur();
                  props.onBlur && props.onBlur(e);
                }}
              >
                {props.options.map((elem, index) => (
                  <option
                    disabled={elem.isDisabled}
                    key={`${props.name}_${elem.value}_${index}`}
                    value={elem.value}
                    className={styles.option}
                  >
                    {elem.label}
                  </option>
                ))}
              </select>
              <div className={styles.trailingIcon}>
                <Icon iconId={IconsEnum.CHEVRON_DOWN} className={`${styles.icon}`} />
              </div>
            </div>
            <span className={styles.error}>
              {error?.message ?? (error?.types && Object.keys(error?.types!).map((key) => error?.types![key]))}
            </span>
          </div>
        )}
      />
    );
  }
  return (
    <div className={styles.container}>
      <label htmlFor={props.label} className={styles.label}>
        {props.label}
      </label>
      <div className={styles.selectWrapper}>
        <select
          id={props.name}
          className={`${styles.select}`}
          value={props.value}
          onChange={(e) => {
            props.onChange && props.onChange(e);
          }}
          onBlur={(e) => {
            props.onBlur && props.onBlur(e);
          }}
        >
          {props.options.map((elem) => (
            <option value={elem.value} key={elem.value}>
              {elem.label}
            </option>
          ))}
        </select>
        <div className={styles.trailingIcon}>
          <Icon iconId={IconsEnum.CHEVRON_DOWN} className={styles.icon} color="var(--color-on-bg-primary)" />
        </div>
      </div>
    </div>
  );
}
